import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useResetPassword } from '@/context/auth'
import { useToast } from '@/hooks/use-toast'
import { ApiError } from '@/lib/http'

const schema = z
  .object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(8, 'At least 8 characters'),
    confirmPassword: z.string().min(1, 'Confirm your password'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof schema>

interface Props {
  token: string
  email?: string
}

export function ResetPasswordForm({ token, email = '' }: Props) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const mutation = useResetPassword()
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email, password: '', confirmPassword: '' },
  })

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({ token, email: values.email, password: values.password })
      toast({ title: 'Password updated', description: 'You can now sign in.' })
      navigate({ to: '/login' })
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errors) {
          for (const [k, msgs] of Object.entries(err.errors)) {
            if (k in errors || k === 'email' || k === 'password') {
              setError(k as keyof FormValues, { message: msgs[0] })
            }
          }
        }
        toast({ title: 'Error', description: err.message, variant: 'destructive' })
      } else {
        toast({ title: 'Error', description: 'An unexpected error occurred', variant: 'destructive' })
      }
    }
  })

  return (
    <Card className="overflow-hidden">
      <CardContent className="grid p-0">
        <form className="p-6 md:p-8" onSubmit={onSubmit}>
          <div className="grid gap-6">
            <div className="flex flex-col items-center text-center">
              <h1 className="text-2xl font-semibold tracking-tight">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Enter and confirm your new password below.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                disabled={isSubmitting}
                {...register('email')}
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                disabled={isSubmitting}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                disabled={isSubmitting}
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
              )}
            </div>
            <Button type="submit" disabled={isSubmitting} className="mt-2">
              {isSubmitting ? 'Updating password...' : 'Update password'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
